import { matchPath } from 'react-router-dom'

import type { ColorMode } from '@repo/ui'
import { PRE_AUTH_ROUTE_PATTERNS } from 'routes'

/**
 * VRG-600 — carries the visitor's colour-mode choice across the origin boundary
 * from vera.inc to platform.vera.inc, and decides whether this app's own stored
 * preference is allowed to speak on the page being entered.
 *
 * WHY A QUERY PARAM. `vera-color-mode` in localStorage is the same key, holding
 * the same `light | dark | system` vocabulary, in both codebases — and it is
 * still useless across the hand-off, because localStorage is partitioned by
 * ORIGIN. vera.inc and platform.vera.inc are different origins, so the two
 * copies never see each other. The website's own ThemeProvider has recorded this
 * as a KNOWN LIMIT from the start, and named a `.vera.inc`-scoped cookie as the
 * alternative. A param was chosen over the cookie on three counts:
 *
 *   - it maps 1:1 onto the actual event ("clicked Log in ON the website"),
 *     whereas a cookie also speaks for arrivals that had nothing to do with
 *     the site;
 *   - it needs no shared-domain configuration, so it works in every
 *     environment including this one, where the two dev servers are
 *     `localhost:3000` and `127.0.0.1:<port>` — unrelated origins on which a
 *     `.vera.inc` cookie cannot be exercised AT ALL, so the cookie version
 *     would have been unverifiable locally;
 *   - it is spoofable, which for a colour scheme costs nothing.
 *
 * THE PARAM IS STRIPPED once read, so it does not sit in the address bar, does
 * not get copied into a shared link, and is not carried into any later
 * navigation as though it were a fresh decision.
 *
 * ⚠️ THIS RUNS AT MODULE LOAD, ON PURPOSE, and that is load-bearing twice over.
 * It has to happen before `VeraThemeProvider`'s `useState` initialiser reads the
 * mode (or the first paint is the wrong colour and then visibly corrects), and
 * before `BrowserRouter` reads `window.location` (or the router captures a
 * `?theme=` that the app would then have to explain in its route handling). The
 * same pattern, for the same reason, is `INITIAL_DEV_SURFACE` in
 * `utils/devSurface` — "captured at module load (page load) BEFORE React Router
 * can redirect".
 */

const THEME_PARAM = 'theme'

/**
 * The colour mode the visitor is looking at on the PRE-AUTH pages of THIS tab, kept in
 * sessionStorage so it survives a full-page round trip away from this origin and back.
 *
 * THE BUG THIS FIXES (Spencer, 2026-10-09): "Log in with social" leaves platform.vera.inc for
 * the API / WorkOS / Google and comes back as a NEW page load on `/auth/callback` (which is
 * where the second-factor challenge renders for SSO). On that load the `?theme=` hand-off was
 * already stripped on the first load, and `ignoreStoredMode` (correctly) ignores the
 * signed-in app's localStorage preference on a pre-auth route — so nothing remembered what
 * the sign-in page had shown, and the verify screen fell back to the OS preference. Sign-in
 * light, verify dark. A sign-in page theme TOGGLE was lost the same way, since the toggle
 * writes only the ignored localStorage key.
 *
 * sessionStorage is the right scope: per-origin AND per-tab, it survives exactly this
 * same-tab cross-origin round trip, and it does not leak to another tab or a later visitor
 * the way the authenticated app's localStorage value would (the VRG-600 concern).
 */
const PRE_AUTH_MODE_KEY = 'vera-pre-auth-color-mode'

export const readPreAuthColorMode = (): ColorMode | null => {
  try {
    const value = window.sessionStorage.getItem(PRE_AUTH_MODE_KEY)
    return isColorModeValue(value) ? value : null
  } catch {
    return null
  }
}

/** Called by the pre-auth layout whenever the shown mode changes (hand-off, toggle). */
export const rememberPreAuthColorMode = (mode: ColorMode): void => {
  try {
    window.sessionStorage.setItem(PRE_AUTH_MODE_KEY, mode)
  } catch {
    // Storage blocked (private mode / policy): the round trip falls back to the OS mode.
  }
}

function isColorModeValue(value: string | null): value is ColorMode {
  return value === 'light' || value === 'dark' || value === 'system'
}

const isColorMode = (value: string | null): value is ColorMode =>
  value === 'light' || value === 'dark' || value === 'system'

function captureAndStripThemeParam(): ColorMode | null {
  if (typeof window === 'undefined') return null

  const url = new URL(window.location.href)
  const raw = url.searchParams.get(THEME_PARAM)
  if (raw === null) return null

  // Strip whatever we found, valid or not. A junk `?theme=purple` is still the
  // site trying to tell us something; leaving it in the bar just makes the
  // address look broken to whoever reads it next.
  url.searchParams.delete(THEME_PARAM)
  window.history.replaceState(
    window.history.state,
    '',
    `${url.pathname}${url.search}${url.hash}`,
  )

  if (!isColorMode(raw)) return null
  rememberPreAuthColorMode(raw)
  return raw
}

const HANDOFF_PARAM_MODE: ColorMode | null = captureAndStripThemeParam()

/** Does this path render one of the routes that by definition has no session? */
export const isPreAuthPath = (pathname: string): boolean =>
  PRE_AUTH_ROUTE_PATTERNS.some(pattern => matchPath(pattern, pathname) !== null)

/**
 * Was the app ENTERED on a pre-auth route?
 *
 * ⚠️ Entry path, not current path, and the difference is a real limit worth
 * naming rather than discovering. A cold load of a protected URL while signed
 * out (`/certificates`, say) is not pre-auth by this measure, so the stored mode
 * applies — and `ProtectedRoute` then bounces the visitor to `/sign-in`, where
 * they see it. That is the same leak this module exists to close, arriving
 * through a different door.
 *
 * It is left open deliberately. Making this reactive to the current route would
 * mean the theme visibly FLIPS partway through that redirect, and a wash from
 * dark to light mid-navigation is a worse artefact than a sign-in page in the
 * wrong scheme. Every arrival the spec actually describes — a link in an email,
 * a click from the website — lands on an auth route directly and is covered.
 */
export const ENTERED_PRE_AUTH: boolean =
  typeof window !== 'undefined' && isPreAuthPath(window.location.pathname)

/**
 * The mode to open with, most explicit first: the website's `?theme=` on THIS navigation,
 * else — only when entering a pre-auth route — whatever this tab's pre-auth pages were last
 * showing (survives the social-login / SSO round trip; see `PRE_AUTH_MODE_KEY`), else null
 * (the provider then uses the OS preference on pre-auth pages, the saved preference elsewhere).
 */
export const HANDOFF_COLOR_MODE: ColorMode | null =
  HANDOFF_PARAM_MODE ?? (ENTERED_PRE_AUTH && typeof window !== 'undefined' ? readPreAuthColorMode() : null)
